"use client";

import { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  name?: string;
  size?: AvatarSize;
}

const sizeStyles: Record<
  AvatarSize,
  { container: string; text: string; px: number }
> = {
  xs: { container: "w-6 h-6", text: "text-[10px]", px: 24 },
  sm: { container: "w-8 h-8", text: "text-[11px]", px: 32 },
  md: { container: "w-10 h-10", text: "text-[13px]", px: 40 },
  lg: { container: "w-12 h-12", text: "text-[15px]", px: 48 },
  xl: { container: "w-16 h-16", text: "text-xl", px: 64 },
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({
  src,
  alt,
  name = "",
  size = "md",
  className = "",
  ...props
}: AvatarProps) {
  const { container, text, px } = sizeStyles[size];
  const initials = getInitials(name || "?");

  return (
    <div
      className={cn(
        "relative inline-flex items-center justify-center rounded-full overflow-hidden shrink-0",
        container,
        !src && "bg-brand-glow-08 border border-brand-glow-30",
        className,
      )}
      {...props}
    >
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={alt || name}
          width={px}
          height={px}
          className="w-full h-full object-cover"
        />
      ) : (
        <span
          className={cn(
            "font-mono font-semibold tracking-[0.02em] text-brand",
            text,
          )}
        >
          {initials}
        </span>
      )}
    </div>
  );
}

export { Avatar, type AvatarProps, type AvatarSize };
